import { useState } from "react";
import { useQuery, keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PaginationControls } from "@/components/PaginationControls";
import { toast } from "sonner";
import { Search, ScrollText, ChevronDown, ChevronUp, Globe, Fingerprint, ShieldCheck, UserPlus, LogIn, Ban, Users, MapPin, X } from "lucide-react";

// Quick human summary of a raw user-agent string.
function deviceSummary(ua = "") {
  const browser =
    /edg\//i.test(ua) ? "Edge" :
    /opr\//i.test(ua) ? "Opera" :
    /chrome\//i.test(ua) ? "Chrome" :
    /firefox\//i.test(ua) ? "Firefox" :
    /safari\//i.test(ua) ? "Safari" : "Unknown";
  const os =
    /windows/i.test(ua) ? "Windows" :
    /android/i.test(ua) ? "Android" :
    /iphone|ipad|ios/i.test(ua) ? "iOS" :
    /mac os/i.test(ua) ? "macOS" :
    /linux/i.test(ua) ? "Linux" : "Unknown";
  const mobile = /mobile|android|iphone/i.test(ua);
  return `${browser} · ${os}${mobile ? " · Mobile" : ""}`;
}

function KV({ k, v }) {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return null;
  const text = typeof v === "object" ? JSON.stringify(v) : String(v);
  return (
    <div className="flex gap-2 text-[11px] font-mono leading-relaxed">
      <span className="text-white/40 shrink-0 min-w-[150px]">{k}</span>
      <span className="text-white/80 break-all">{text}</span>
    </div>
  );
}

function DetailPanel({ r, onOpenIp }) {
  const ci = r.client_info || {};
  const loc = r.location || {};
  const rev = loc.reported_by_client || {};
  const ipLoc = loc.ip_lookup || {};
  return (
    <div className="grid md:grid-cols-2 gap-5 px-5 py-4 bg-black/30" data-testid={`tos-record-detail-${r.id}`}>
      <div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/80 mb-2">
          <Globe className="h-3.5 w-3.5" /> Network Evidence
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-1">
            <Button size="sm" variant="outline" data-testid={`tos-view-ip-profiles-${r.id}`}
              onClick={() => onOpenIp?.(r.ip, "profiles")}
              className="h-7 px-2 text-[11px] border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10">
              <Users className="h-3 w-3 mr-1" /> Profiles for {r.ip}
            </Button>
            <Button size="sm" variant="outline" data-testid={`tos-ban-ip-${r.id}`}
              onClick={() => onOpenIp?.(r.ip, "ban")}
              className="h-7 px-2 text-[11px] border-red-400/40 text-red-300 hover:bg-red-400/10">
              <Ban className="h-3 w-3 mr-1" /> Ban this IP
            </Button>
          </div>
          <KV k="ip" v={r.ip} />
          <KV k="ip_chain (XFF)" v={r.ip_chain} />
          <KV k="direct_ip" v={r.direct_ip} />
          <KV k="country" v={r.country} />
          <KV k="user_agent" v={r.user_agent} />
          {Object.entries(r.headers || {}).map(([k, v]) => <KV key={k} k={`hdr:${k}`} v={v} />)}
        </div>
        {(loc.precision || rev.lat || ipLoc.lat) && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 mb-2">
              <MapPin className="h-3.5 w-3.5" /> Location · {loc.precision || "—"}
              {loc.consent_denied && <span className="ml-1 text-amber-300 text-[9px] uppercase tracking-[0.2em]">consent denied</span>}
            </div>
            <div className="space-y-1">
              <KV k="lat / lng" v={rev.lat != null ? `${rev.lat}, ${rev.lng}${rev.accuracy_m ? ` (±${Math.round(rev.accuracy_m)}m)` : ""}` : (ipLoc.lat != null ? `${ipLoc.lat}, ${ipLoc.lng} (ip)` : null)} />
              <KV k="address" v={rev.address} />
              <KV k="city" v={rev.city || ipLoc.city} />
              <KV k="region" v={rev.region || ipLoc.region} />
              <KV k="postal_code" v={rev.postal_code || ipLoc.postal_code} />
              <KV k="country" v={rev.country || ipLoc.country} />
              <KV k="country_code" v={rev.country_code || ipLoc.country_code} />
              <KV k="reverse_source" v={rev.reverse_source || ipLoc.reverse_source} />
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-2">
          <Fingerprint className="h-3.5 w-3.5" /> Device Fingerprint
        </div>
        {Object.keys(ci).length === 0 ? (
          <div className="text-white/35 text-xs font-mono">No client fingerprint captured (older record or blocked probe).</div>
        ) : (
          <div className="space-y-1">
            {Object.entries(ci).map(([k, v]) => <KV key={k} k={k} v={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Two-mode dialog opened from a row action — either lists every profile that
 * has ever accepted from this IP ("profiles"), or asks for a reason + optional
 * expiry and POSTs a ban ("ban"). Bans use the existing `/api/admin/bans` route.
 */
function IpActionDialog({ ip, mode, open, onOpenChange }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [minutes, setMinutes] = useState("");

  const { data: profiles } = useQuery({
    queryKey: ["admin-ip-profiles", ip],
    queryFn: async () => (await api.get(`/admin/ip-profiles?ip=${encodeURIComponent(ip)}`)).data,
    enabled: !!ip && open,
    staleTime: 15_000,
  });

  const ban = useMutation({
    mutationFn: async () => (await api.post("/admin/bans", {
      ip,
      reason: reason.trim() || null,
      minutes: minutes ? parseInt(minutes, 10) : 0,
    })).data,
    onSuccess: () => {
      toast.success(`Banned ${ip}`);
      qc.invalidateQueries({ queryKey: ["admin-ip-profiles", ip] });
      qc.invalidateQueries({ queryKey: ["admin-bans"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, "Ban failed")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[hsl(222_22%_7%)] border-white/10" data-testid="admin-ip-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            {mode === "ban" ? <Ban className="h-4 w-4 text-red-300" /> : <Users className="h-4 w-4 text-cyan-300" />}
            {mode === "ban" ? `Ban IP · ${ip}` : `Profiles from ${ip}`}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {mode === "ban"
              ? "The banned message shown to the user is generic. Reason is admin-only."
              : `${profiles?.total ?? 0} account(s) have accepted the Terms from this IP.${profiles?.is_banned ? " · IP is currently banned." : ""}`}
          </DialogDescription>
        </DialogHeader>

        {mode === "profiles" ? (
          <div className="max-h-[50vh] overflow-y-auto space-y-1.5" data-testid="admin-ip-profiles-list">
            {(profiles?.profiles || []).length === 0 ? (
              <div className="text-white/40 text-sm">No profiles found for this IP.</div>
            ) : (
              (profiles?.profiles || []).map((p) => (
                <div key={p.user_id} data-testid={`admin-ip-profile-${p.user_id}`}
                  className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <div>
                      <div className="text-white text-sm">{p.display_name || "—"} <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ml-1 ${p.role === "admin" ? "text-amber-300" : "text-cyan-300/70"}`}>{p.role || "collector"}</span></div>
                      <div className="font-mono text-[11px] text-white/50">{p.email || p.user_id.slice(0, 8)}</div>
                    </div>
                    <div className="text-right font-mono text-[10px] text-white/50">
                      <div>{p.acceptance_count} accept{p.acceptance_count === 1 ? "" : "s"}</div>
                      <div className="text-white/35">last {new Date(p.last_seen_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Reason (admin-only)</label>
              <Input data-testid="admin-ip-ban-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. shared network abusing trades" className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Duration (minutes — leave blank for permanent)</label>
              <Input data-testid="admin-ip-ban-minutes" type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)}
                placeholder="0 = permanent" className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div className="text-[11px] text-white/40">
              This bans the IP for new logins, registrations and ToS acceptances. Existing sessions are unaffected until they hit an auth-gated route.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="admin-ip-action-cancel" className="text-white/70">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
          {mode === "ban" && (
            <Button onClick={() => ban.mutate()} disabled={ban.isPending} data-testid="admin-ip-action-confirm-ban"
              className="bg-red-400 text-black hover:bg-red-300">
              {ban.isPending ? "Banning…" : <><Ban className="h-4 w-4 mr-1" /> Ban this IP</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTos() {
  const [search, setSearch] = useState("");
  const [context, setContext] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(null);
  const [ipDialog, setIpDialog] = useState({ open: false, ip: null, mode: "profiles" });

  const openIp = (ip, mode) => setIpDialog({ open: true, ip, mode: mode || "profiles" });

  const { data } = useQuery({
    queryKey: ["admin-tos", search, context, page, pageSize],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page + 1), page_size: String(pageSize) });
      if (search.trim()) p.set("q", search.trim());
      if (context !== "all") p.set("context", context);
      return (await api.get(`/admin/tos/acceptances?${p}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const current = data?.current_version;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl text-white tracking-[0.06em]">TOS ACCEPTANCES</h1>
          <p className="text-white/45 text-xs mt-1">Legal evidence ledger — every recorded agreement with full IP, header, and device-fingerprint trail. Retained for identity and criminal-verification purposes.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <div className="relative flex-1 min-w-[200px] lg:flex-initial lg:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="tos-admin-search" placeholder="Search email / name / IP / UA / country…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-[hsl(220_18%_12%)] border-white/10 w-full" />
          </div>
          <Select value={context} onValueChange={(v) => { setContext(v); setPage(0); }}>
            <SelectTrigger data-testid="tos-admin-context-filter" className="bg-[hsl(220_18%_12%)] border-white/10 w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
              <SelectItem value="all">All contexts</SelectItem>
              <SelectItem value="register">Registration</SelectItem>
              <SelectItem value="login_gate">Login gate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          ["Current build", `V ${current ?? "—"}`, "text-cyan-300"],
          ["Total acceptances", data?.total_all ?? "—", "text-white"],
          ["Unique signers", data?.unique_users ?? "—", "text-emerald-300"],
        ].map(([label, value, color]) => (
          <div key={label} className="hud-pill rounded-xl px-4 py-3">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">{label}</div>
            <div className={`font-display text-xl mt-0.5 ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      <div className="hud-pill rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="tos-admin-table">
            <thead className="bg-white/5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              <tr>
                <th className="px-4 py-3">Accepted at</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Context</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const stale = current && r.version !== current;
                const open = expanded === r.id;
                return (
                  <>
                    <tr key={r.id} data-testid={`tos-record-row-${r.id}`} onClick={() => setExpanded(open ? null : r.id)}
                      className={`border-t border-white/5 cursor-pointer transition-colors ${open ? "bg-cyan-400/5" : "hover:bg-white/[0.03]"}`}>
                      <td className="px-4 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{new Date(r.accepted_at).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm">{r.user_name || "—"}</div>
                        <div className="font-mono text-[10px] text-white/40">{r.user_email || r.user_id?.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] ${r.context === "register" ? "text-emerald-300" : "text-cyan-300"}`}>
                          {r.context === "register" ? <UserPlus className="h-3 w-3" /> : <LogIn className="h-3 w-3" />}
                          {r.context === "register" ? "Register" : "Login"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5 border ${stale ? "text-amber-300 border-amber-300/40" : "text-cyan-300 border-cyan-300/30"}`}>
                          V {r.version}{stale ? " · old" : ""}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70 whitespace-nowrap">
                        {r.ip}{r.country ? <span className="text-white/35"> · {r.country}</span> : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-white/55 whitespace-nowrap">{deviceSummary(r.user_agent)}</td>
                      <td className="px-4 py-3 text-white/40">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</td>
                    </tr>
                    {open && <tr key={`${r.id}-detail`} className="border-t border-white/5"><td colSpan={7} className="p-0"><DetailPanel r={r} onOpenIp={openIp} /></td></tr>}
                  </>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-white/40">
                  <ScrollText className="h-6 w-6 mx-auto mb-2 opacity-40" /> No acceptance records match.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PaginationControls page={page} total={data?.total || 0} pageSize={pageSize}
        onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} testidPrefix="tos-admin" />

      <div className="mt-4 flex items-start gap-2 text-[11px] text-white/35 font-mono">
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Records marked "old" were accepted under a prior build version — those users are automatically re-gated until they accept V {current ?? "current"}.
      </div>

      <IpActionDialog
        ip={ipDialog.ip}
        mode={ipDialog.mode}
        open={ipDialog.open}
        onOpenChange={(o) => setIpDialog((s) => ({ ...s, open: o }))}
      />
    </div>
  );
}
